import type {
  ResolutionEvent,
  ResolutionFrame,
} from "../../../shared/core/index.js";
import type { SeCueId } from "../../audio/AudioCue.js";

export interface BattleAnimationPort {
  animateMove(event: ResolutionEvent): Promise<void>;
  animateMerge(event: ResolutionEvent): Promise<void>;
  animateAppear(event: ResolutionEvent): Promise<void>;
  animateExit(event: ResolutionEvent): Promise<void>;
  animateHappiness(event: ResolutionEvent): Promise<void>;
  syncFrame(frame: ResolutionFrame): void;
  presentResolutionEvent(event: ResolutionEvent, index: number, total: number): void;
  announce(message: string): void;
  playAudioCue(cue: SeCueId): void;
}

export class AnimationDirector {
  private playing = false;

  public constructor(private readonly port: BattleAnimationPort) {}

  public get isPlaying(): boolean {
    return this.playing;
  }

  public async play(events: readonly ResolutionEvent[]): Promise<void> {
    if (this.playing) return;
    this.playing = true;

    try {
      for (const [index, event] of events.entries()) {
        this.port.presentResolutionEvent(event, index, events.length);
        await this.playEvent(event);
        this.port.syncFrame(event.frame);
      }
    } finally {
      this.playing = false;
    }
  }

  private async playEvent(event: ResolutionEvent): Promise<void> {
    const audioCue = getAudioCue(event);
    if (audioCue) this.port.playAudioCue(audioCue);

    switch (event.type) {
      case "move":
      case "drift":
        if (event.objectId && event.to) {
          this.port.announce(event.text);
          await this.port.animateMove(event);
        }
        return;
      case "stage":
        this.port.announce(event.text);
        if (event.objectId && event.to) {
          await this.port.animateMove(event);
        } else {
          await pause(360);
        }
        return;
      case "merge":
      case "rescue":
        this.port.announce(event.text);
        await this.port.animateMerge(event);
        return;
      case "appear":
      case "return":
        this.port.announce(event.text);
        await this.port.animateAppear(event);
        return;
      case "exit":
      case "suspend":
        this.port.announce(event.text);
        await this.port.animateExit(event);
        return;
      case "happiness":
      case "comfort":
        this.port.announce(event.text);
        await this.port.animateHappiness(event);
        return;
      case "reveal":
      case "resonance":
      case "card":
      case "devotion":
      case "ignite":
      case "replay":
      case "frame":
      case "preserve":
      case "guidance":
      case "freeze":
      case "invert":
      case "hold":
      case "grow":
      case "prune":
      case "fizzle":
      case "result":
        this.port.announce(event.text);
        await pause(360);
        return;
      default:
        return;
    }
  }
}

function getAudioCue(event: ResolutionEvent): SeCueId | null {
  switch (event.type) {
    case "reveal":
    case "card":
    case "devotion":
      return "card_reveal";
    case "appear":
    case "return":
    case "grow":
      return "object_appear";
    case "move":
    case "drift":
    case "stage":
    case "invert":
      return "object_move";
    case "merge":
    case "rescue":
    case "resonance":
      return "object_merge";
    case "exit":
    case "suspend":
    case "prune":
      return "object_exit";
    case "ignite":
    case "frame":
    case "preserve":
    case "guidance":
    case "replay":
      return "card_reveal";
    case "happiness":
    case "comfort":
      return (event.after ?? 0) >= (event.before ?? 0)
        ? "happiness_up"
        : "happiness_down";
    default:
      return null;
  }
}

function pause(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
